import { memo, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  MiAutocomplete,
  MiButton,
  MiDetailFields,
  MiIcon,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  CONTACT_CARD_EDIT_MODES,
  PREFERRED_CONTACT_METHOD,
} from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import { getFullName } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiMagnify, mdiPlus } from "@mdi/js";
import {
  Box,
  Collapse,
  Divider,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { MiContactCard } from "../MiContactCard/MiContactCard";
import { useContactsSearchViewModel } from "./useContactsSearchViewModel";

const DEFAULT_CONTACT = {
  accountId: "",
  id: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  title: "",
  preferredContactMethod: PREFERRED_CONTACT_METHOD.Phone,
  label: "",
  isPrimary: false,
  isVip: false,
  dependentType: "",
};

const schema = z.object({
  dependentType: z.string().min(1, "Relationship Type is required"),
});

export const ContactsSearch = ({
  defaultAccountId = null,
  accountTypes = null,
  accountIds = defaultAccountId ? [defaultAccountId] : null,
  placeholder = "Search Contacts",
  contacts = [],
  setContacts,
  referenceIds,
  onRefresh,
  onCancelClick,
  setIsContactEditing = () => {},
}) => {
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const {
    activeCategories,
    contacts: allContacts,
    getContacts,
    getContactCategoriesOptions,
    addContactRelationship,
  } = useContactsSearchViewModel({
    accountIds,
    referenceIds,
    onRefresh,
    selectedContacts: contacts,
    setSelectedContacts: setContacts,
  });

  const categoryOptions = useMemo(() => {
    const contactCategoriesCount = contacts.reduce((acc, contact) => {
      const { dependentType } = contact;
      acc[dependentType] = (acc[dependentType] || 0) + 1;
      return acc;
    }, {});

    return activeCategories.map(({ label, max }) => ({
      value: label,
      label,
      max,
      disabled:
        (contactCategoriesCount[label] >= max || max === 0) && max !== -1,
    }));
  }, [activeCategories, contacts]);
  const {
    control,
    formState: { errors },
    reset,
    getValues,
    setValue,
    setError,
    watch,
    clearErrors,
  } = useForm({
    mode: "onChange",
    defaultValues: { ...DEFAULT_CONTACT },
    resolver: zodResolver(schema),
  });
  const id = watch("id");

  const relationshipFields = [
    {
      label: "Relationship Type",
      name: "dependentType",
      fieldType: "select",
      shrink: true,
      options: categoryOptions,
      required: true,
      formControl: control,
      handleChange: (e) => {
        setValue("dependentType", e.target.value);
        setValue("isPrimary", false);
        if (
          contacts.some(
            (contact) =>
              contact.dependentType === e.target.value && contact.id === id
          )
        ) {
          setError("dependentType", {
            message: "Contact already exists with this relationship type.",
          });
        } else {
          clearErrors();
        }
      },
    },
    {
      label: "Primary Contact",
      name: "isPrimary",
      fieldType: "switch",
      formControl: control,
    },
  ];

  const handleAutoCompleteOpen = () => setIsAutocompleteOpen(true);

  const handleAutoCompleteClose = () => setIsAutocompleteOpen(false);

  const handleOpenAddContact = () => {
    handleAutoCompleteClose();

    if (isAddContactOpen) {
      return;
    }

    setSearch("");
    reset({ ...DEFAULT_CONTACT });
    setIsAddContactOpen(true);
  };

  const handleCloseAddContact = () => setIsAddContactOpen(false);

  const handleSearch = (_e, value) => {
    setSearch(value);
  };

  const addContact = () => {
    const contact = getValues();

    if (referenceIds) {
      addContactRelationship(contact);
      return;
    }

    setContacts((prev) => [
      ...prev,
      { ...contact, label: contact.dependentType },
    ]);
    reset({ ...DEFAULT_CONTACT });
  };

  const deleteContact = (i) =>
    setContacts((prev) => prev.filter((_, index) => index !== i));

  const handleContactSelect = (value) => {
    handleAutoCompleteClose();
    handleCloseAddContact();
    reset({
      ...value,
      dependentType: "",
      isPrimary: null,
    });
  };

  const handleCreateContact = (contact) => {
    handleCloseAddContact();
    getContacts(debouncedSearch);
    reset({
      ...contact,
      dependentType: "",
      isPrimary: null,
    });
  };

  useEffect(() => {
    setIsContactEditing(Boolean(id) || isAddContactOpen);
  }, [id, isAddContactOpen, setIsContactEditing]);

  useEffect(() => {
    getContactCategoriesOptions();
  }, []);

  useEffect(() => {
    getContacts(debouncedSearch);
  }, [debouncedSearch, defaultAccountId, contacts.length]);

  return (
    <>
      {!isAddContactOpen && (
        <MiAutocomplete
          size="small"
          fullWidth
          disableClearable
          forcePopupIcon={false}
          open={isAutocompleteOpen}
          filterOptions={(options) => options}
          options={allContacts}
          getOptionLabel={(value) => {
            const contact = allContacts.find(
              ({ id }) => id === value || id === value?.id
            );

            return contact
              ? getFullName(contact.firstName, contact.lastName)
              : "";
          }}
          value={null}
          inputValue={search}
          getOptionKey={({ id }) => id}
          onInputChange={handleSearch}
          isOptionEqualToValue={(value, option) => option === value.id}
          onChange={(_e, value) => handleContactSelect(value)}
          onOpen={handleAutoCompleteOpen}
          onClose={handleAutoCompleteClose}
          renderOption={(
            { ...props },
            { id, firstName, lastName, accountName }
          ) => (
            <MenuItem key={id} {...props}>
              <Typography lineHeight={2}>
                {`${getFullName(firstName, lastName)} | ${
                  accountName || "Internal"
                }`}
              </Typography>
            </MenuItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              sx={{ backgroundColor: "background.paper" }}
              InputProps={{
                ...params.InputProps,
                "data-testid": "mi-autocomplete-input-search",
                value: search,
                startAdornment: (
                  <InputAdornment position="end">
                    <MiIcon path={mdiMagnify} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          slotProps={{
            paper: { onMouseDown: handleOpenAddContact },
          }}
          PaperComponent={AutocompletePaper}
        />
      )}
      <Box>
        <Collapse in={Boolean(id)} unmountOnExit>
          <Box
            display="flex"
            flexDirection="column"
            paddingTop={2}
            backgroundColor="common.white"
          >
            <MiContactCard readOnly elevation={0} contact={getValues()} />
            <Box paddingX={2} display="flex" flexDirection="column" gap={2}>
              <Divider />
              <MiDetailFields
                detailFields={relationshipFields}
                control={control}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2} p={2}>
              <MiButton
                color={BUTTON_COLORS.BLACK}
                title="Cancel"
                variant="text"
                onClick={onCancelClick || (() => reset({ ...DEFAULT_CONTACT }))}
              />
              <MiButton
                title="Add Contact"
                variant="contained"
                disabled={!getValues("dependentType") || errors.dependentType}
                onClick={addContact}
              />
            </Box>
          </Box>
        </Collapse>
        <Collapse in={isAddContactOpen} unmountOnExit>
          <MiContactCard
            accountIds={accountIds}
            defaultAccountId={defaultAccountId}
            accountTypes={accountTypes}
            editMode={CONTACT_CARD_EDIT_MODES.CONTACT}
            elevation={0}
            onRefresh={handleCreateContact}
            onCloseEdit={onCancelClick || handleCloseAddContact}
          />
        </Collapse>
      </Box>
      {contacts.length > 0 && (
        <Box display="flex" width="100%" flexWrap="wrap" gap={2} mt={2}>
          {contacts.map((contact, i) => (
            <Box key={contact.id} minWidth="49.3%">
              <MiContactCard
                enableDeleteRelationship
                contact={contact}
                onRefresh={() => getContacts(debouncedSearch)}
                editMode={CONTACT_CARD_EDIT_MODES.CONTACT}
                onDelete={() => deleteContact(contact.id)}
                onDeleteRelationshipOverwrite={() => deleteContact(i)}
              />
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

const AutocompletePaper = memo(({ children, onMouseDown, ...props }) => (
  <Paper
    {...props}
    sx={{
      ...props.sx,
      overflowX: "auto",
    }}
  >
    <Box display="flex" flexDirection="column" minWidth="fit-content">
      <MiButton
        fullWidth
        title="Create New Contact"
        startIcon={{ path: mdiPlus }}
        sx={{
          borderRadius: 0,
          justifyContent: "flex-start",
          color: "blue.main",
          pl: 4,
          mb: -2,
        }}
        onMouseDown={onMouseDown}
      />
      {children}
    </Box>
  </Paper>
));
